import type { AlertService } from '.';
import type { ConditionAlertModel } from './AlertModel';
import type { ConditionAlertModelImpl } from './AlertModel/impl';
import type { AlertServiceProvider } from './AlertServiceProvider';
import type { IdeaAlertModel } from './IdeaAlertModel';
import type { IdeaAlertModelImpl } from './IdeaAlertModel/impl';
import type {
    AlertWorkerApi,
    AlertWorkerEvent,
    AlertServiceConnectionStatus,
    AlertTriggeredPayload,
    AlertDefinitionWire,
    NewAlertDefinition,
} from './types';
import { DateTimeFormatter, Instant, ZoneId } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import { effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { AlertDefinition as AlertDefinitionSchema } from '@thinkalpha/platform-ws-client/contracts/alerts.js';
import type { AlertDefinition, AlertNotification } from '@thinkalpha/platform-ws-client/contracts/alerts.js';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { toDisposable } from '@thinkalpha/ui-core/toDisposable';
import type { ConcreteIndicatorViewModel } from '@thinkalpha/ui-types/contracts/dictionary-view-model';
import * as Comlink from 'comlink';
import type { Remote } from 'comlink';
import i18next from 'i18next';
import { injectable, inject, skipAutoDispose } from 'src/features/ioc';
import { alwaysPlayAlertSounds } from 'src/lib/featureFlags/flags/alwaysPlayAlertSounds';
import { useNextGenProxy } from 'src/lib/featureFlags/flags/useNextGenProxy';
import { instrumentToString } from 'src/lib/instrumentUtils';
import { parseNotifications } from 'src/models/AlertsWidgetModel/notifications';
import { formatConditionForDisplay, formatConditionForSpeech } from 'src/models/AlertsWidgetModel/util';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getAlertingIndicatorsQuery } from 'src/queries/dictionary';
import type { QueryCacheUserChangeEvent, QueryCacheUserServiceDataSource } from 'src/services/QueryCacheUserService';
import type { ReactBindings } from 'src/types/bindings';

export function spellOutSymbol(symbol: string): string {
    return symbol.split('').join(' ');
}

/**
 * Thrown when an alert operation is requested but the underlying worker is not
 * connected. The current connection status is attached so observability can
 * distinguish the failure modes (e.g. permanent `error` vs transient `disconnected`).
 */
export class AlertWorkerUnavailableError extends Error {
    readonly connectionStatus: AlertServiceConnectionStatus;
    constructor(connectionStatus: AlertServiceConnectionStatus) {
        super('Worker not initialized');
        this.name = 'AlertWorkerUnavailableError';
        this.connectionStatus = connectionStatus;
    }
}

/**
 * Bounded exponential backoff schedule for retrying worker initialization
 * after `provider.getWorkerProxy()` throws. Times in milliseconds.
 */
const WORKER_INIT_RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 30_000] as const;
const WORKER_UNAVAILABLE_SOURCE_KEY = 'alerts:worker-unavailable';
const activeWorkerUnavailableNotifications = new Set<string>();

/**
 * Show a toast notification. On web, dispatches to the Redux toast system.
 * On Electron, sends a native desktop notification via IPC to the main process.
 */
function showToastNotification(store: { dispatch: (action: any) => void }, message: string, sourceKey: string): void {
    if (APP_ELECTRON) {
        window.electron!.app.showNotification('Alert', message);
    } else {
        store.dispatch({
            type: 'toast::enqueue',
            toast: {
                message,
                group: 'alerts',
                sourceKey,
            },
        });
    }
}

interface SyncableModel<TDef> extends Disposable {
    readonly id: string;
    updateDefinition(definition: TDef): void;
    initFromDefinition(definition: TDef): void;
}

function syncModelList<TDef extends { id: string }, TModel extends SyncableModel<TDef>>(
    definitions: TDef[],
    currentModels: TModel[],
    factory: () => TModel,
): TModel[] {
    const currentMap = new Map<string, TModel>(currentModels.map((m) => [m.id, m]));
    const seenIds = new Set<string>();
    const result: TModel[] = [];

    for (const definition of definitions) {
        seenIds.add(definition.id);
        const existing = currentMap.get(definition.id);
        if (existing) {
            existing.updateDefinition(definition);
            result.push(existing);
        } else {
            const model = factory();
            model.initFromDefinition(definition);
            result.push(model);
        }
    }

    for (const model of currentModels) {
        if (!seenIds.has(model.id)) {
            model[Symbol.dispose]();
        }
    }

    return result;
}

@injectable()
export class AlertServiceImpl implements AlertService, Disposable {
    readonly [skipAutoDispose] = true;
    readonly #disposableStack = new DisposableStack();
    readonly #conditionAlerts = signal<ConditionAlertModelImpl[]>([]);
    readonly #ideaAlerts = signal<IdeaAlertModelImpl[]>([]);
    readonly #connectionStatus = signal<AlertServiceConnectionStatus>('disconnected');
    readonly #suppressNotifications = signal(false);
    readonly #audioCache = new Map<string, HTMLAudioElement>();
    #isPrimary = !APP_ELECTRON;
    readonly #alwaysPlaySounds: ReadonlySignal<boolean>;
    readonly #useNextGenProxy: ReadonlySignal<boolean>;
    readonly #alertingIndicatorsQuery: QueryObserverModel<ConcreteIndicatorViewModel[]>;
    readonly #voicesReady: Promise<SpeechSynthesisVoice[]>;

    #workerProxy: Remote<AlertWorkerApi> | null = null;
    #unsubscribe: (() => void) | null = null;
    #currentWorkerId: string | undefined = undefined;
    #workerConnectOp = new DisposableBox<Disposable>();

    constructor(
        @inject('AlertServiceProvider') private readonly provider: AlertServiceProvider,
        @inject('Store') private readonly store: ReactBindings['Store'],
        @inject('Logger') private logger: ReactBindings['Logger'],
        @inject('ConditionAlertModelFactory')
        private readonly conditionAlertModelFactory: ReactBindings['ConditionAlertModelFactory'],
        @inject('IdeaAlertModelFactory') private readonly ideaAlertModelFactory: ReactBindings['IdeaAlertModelFactory'],
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('FeatureFlagService') featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('QueryCacheUserServiceDataSource')
        queryCacheUserServiceDataSource: QueryCacheUserServiceDataSource,
    ) {
        this.logger = logger.getSubLogger({ name: 'AlertService' });
        this.#alwaysPlaySounds = featureFlagService.getSignal(alwaysPlayAlertSounds);
        this.#useNextGenProxy = featureFlagService.getSignal(useNextGenProxy);
        // Initialize alerting indicators query for looking up indicator names
        this.#alertingIndicatorsQuery = queryObserverModelFactory(getAlertingIndicatorsQuery());
        this.#disposableStack.use(this.#alertingIndicatorsQuery);

        // Pre-load TTS voices — resolves immediately if voices are already available,
        // otherwise waits for the voiceschanged event (with a timeout fallback)
        this.#voicesReady = this.#loadVoices();

        // Connect to worker for initial user (if logged in)
        const initialUserId = this.#getEffectiveUserId();
        const initialToken = this.#getCurrentToken();
        if (initialUserId) {
            this.#connectToWorker(initialUserId, initialToken);
        }

        // Track the effective user ID to detect user changes (vs token refreshes)
        let previousUserId = initialUserId;
        let previousToken = initialToken;

        // Subscribe to Redux store changes to detect auth changes
        const unsubscribeStore = this.store.subscribe(() => {
            const currentUserId = this.#getEffectiveUserId();
            const currentToken = this.#getCurrentToken();

            const userChanged = currentUserId !== previousUserId;
            const tokenChanged = currentToken !== previousToken;

            if (!userChanged && !tokenChanged) return;

            previousUserId = currentUserId;
            previousToken = currentToken;

            if (userChanged) {
                this.logger.info('User changed', { previousUserId, currentUserId });
                // Tear down old worker connection and connect to new user's worker
                this.#teardownWorker();
                if (currentUserId) {
                    this.#connectToWorker(currentUserId, currentToken);
                }
            } else if (tokenChanged) {
                if (currentToken) {
                    if (this.#workerProxy) {
                        if (TRACE) {
                            this.logger.trace('Token refreshed for same user');
                        }
                        // Same user, just refresh token — worker reacts via effects
                        this.#workerProxy.updateToken(currentToken);
                    } else if (currentUserId) {
                        // Worker was torn down (e.g. token was briefly null during refresh),
                        // but user is still the same — reconnect
                        this.logger.info('Reconnecting worker after token restoration', {
                            userId: currentUserId,
                        });
                        this.#connectToWorker(currentUserId, currentToken);
                    }
                } else {
                    // Token cleared but userId hasn't changed yet (logout in progress).
                    // Don't send updateToken(null) — other tabs sharing this worker still need it.
                    // Just teardown our local connection; the userId change will follow.
                    this.logger.info('Token cleared for current user, tearing down local connection');
                    this.#teardownWorker();
                }
            }
        });
        this.#disposableStack.defer(unsubscribeStore);
        this.#disposableStack.defer(
            queryCacheUserServiceDataSource.subscribe((event) => {
                void this.#notifyWorkerQueryCacheUserChange(event);
            }),
        );

        // Relay `use-nextgen-proxy` changes to the connected worker so its table connections use the
        // same transport as the main thread. The worker has no FeatureFlagService of its own. The
        // effect reads only the flag signal (not the non-reactive `#workerProxy`), so a fresh
        // connection also gets the current value pushed explicitly in `#connectToWorker`.
        this.#disposableStack.defer(
            effect(() => {
                const useNextGen = this.#useNextGenProxy.value;
                void Promise.resolve(this.#workerProxy?.updateUseNextGenProxy(useNextGen)).catch((error) => {
                    this.logger.warn('Failed to relay use-nextgen-proxy to worker', { error });
                });
            }),
        );

        // Clean up all alert models on dispose
        this.#disposableStack.defer(() => {
            for (const model of this.#conditionAlerts.value) {
                model[Symbol.dispose]();
            }
            for (const model of this.#ideaAlerts.value) {
                model[Symbol.dispose]();
            }
        });

        // Clean up worker on dispose
        this.#disposableStack.defer(() => {
            this.#teardownWorker();
        });

        this.logger.info('AlertService initialized');
    }

    get conditionAlerts(): ReadonlySignal<ConditionAlertModel[]> {
        return this.#conditionAlerts;
    }

    get ideaAlerts(): ReadonlySignal<IdeaAlertModel[]> {
        return this.#ideaAlerts;
    }

    get connectionStatus(): ReadonlySignal<AlertServiceConnectionStatus> {
        return this.#connectionStatus;
    }

    get suppressNotifications() {
        return this.#suppressNotifications;
    }

    setPrimary(): void {
        this.#isPrimary = true;
        this.logger.info('AlertService marked as primary notification context');
    }

    #getCurrentToken(): string | null {
        const auth = this.store.getState().auth;
        return auth.masqAccessToken?.token ?? auth.accessToken?.token ?? null;
    }

    #getEffectiveUserId(): string | undefined {
        const auth = this.store.getState().auth;
        // Effective user is masqUser if masquerading, otherwise the logged-in user
        return (auth.masqUser ?? auth.user)?.id;
    }

    /**
     * Connect to the worker for the given user.
     * In SharedWorker mode, each userId gets its own worker instance.
     *
     * On failure, schedules a bounded backoff retry so a transient
     * `provider.getWorkerProxy()` failure (e.g. SharedWorker unsupported,
     * worker script blocked) self-heals instead of leaving the service
     * permanently in `error` state.
     */
    #connectToWorker(userId: string, token: string | null, attempt: number = 0): void {
        if (attempt === 0) {
            // Cancel any in-flight retry timer
            this.#workerConnectOp.clear();
        }

        try {
            this.#workerProxy = this.provider.getWorkerProxy(userId);
            this.#currentWorkerId = userId;
            activeWorkerUnavailableNotifications.delete(WORKER_UNAVAILABLE_SOURCE_KEY);
            // Clear any stale `error` status from a previous attempt so signals
            // bound to connectionStatus reflect the live proxy immediately
            // rather than waiting for the worker's first `state-changed` event.
            if (this.#connectionStatus.value === 'error') {
                this.#connectionStatus.value = 'connecting';
            }

            // Subscribe to worker events using Comlink.proxy() for the callback
            const unsubscribePromise = this.#workerProxy.subscribe(
                Comlink.proxy(async (event: AlertWorkerEvent) => this.#handleEvent(event)),
            );
            // Store unsubscribe once the Comlink round-trip completes
            void Promise.resolve(unsubscribePromise).then(
                (unsub) => {
                    // Guard: only store if we haven't switched users in the meantime
                    if (this.#currentWorkerId === userId) {
                        this.#unsubscribe = unsub;
                    }
                },
                (error) => {
                    if (this.#currentWorkerId !== userId) return;
                    this.#handleWorkerConnectFailure(userId, this.#getCurrentToken() ?? token, attempt, error);
                },
            );

            // Set initial token (non-blocking)
            void Promise.resolve(this.#workerProxy.updateToken(token)).catch((error) => {
                if (this.#currentWorkerId !== userId) return;
                this.#handleWorkerConnectFailure(userId, this.#getCurrentToken() ?? token, attempt, error);
            });

            // Push the current proxy transport so the fresh worker matches the main thread; the
            // constructor effect relays any later `use-nextgen-proxy` changes.
            void Promise.resolve(this.#workerProxy.updateUseNextGenProxy(this.#useNextGenProxy.value)).catch(
                (error) => {
                    this.logger.warn('Failed to send initial use-nextgen-proxy to worker', { error });
                },
            );
            this.#workerConnectOp.clear();
        } catch (error) {
            this.#handleWorkerConnectFailure(userId, token, attempt, error);
        }
    }

    #handleWorkerConnectFailure(userId: string, token: string | null, attempt: number, error: unknown): void {
        if (this.#unsubscribe) {
            this.#unsubscribe();
            this.#unsubscribe = null;
        }
        this.#workerProxy = null;
        this.#currentWorkerId = undefined;
        this.#connectionStatus.value = 'error';

        const lastIdx = WORKER_INIT_RETRY_DELAYS_MS.length - 1;
        const cappedAttempt = Math.min(attempt, lastIdx);
        const delay = WORKER_INIT_RETRY_DELAYS_MS[cappedAttempt]!;

        // Log once when we first hit the cap so prolonged failure surfaces
        // as a structured signal in observability.
        if (attempt === WORKER_INIT_RETRY_DELAYS_MS.length) {
            this.logger.error('Worker init still failing after max backoff reached', {
                userId,
                attempts: attempt,
                error,
            });
        }

        this.#workerConnectOp.next(() => {
            const timer = setTimeout(
                () => this.#connectToWorker(userId, this.#getCurrentToken() ?? token, attempt + 1),
                delay,
            );
            return toDisposable(() => clearTimeout(timer));
        });
    }

    /**
     * Convenience helper that returns the current worker proxy or throws a
     * typed error so callers (and the worker-call wrapper) can branch on it.
     */
    #getWorkerProxy(): Remote<AlertWorkerApi> {
        if (!this.#workerProxy) {
            this.#notifyWorkerUnavailable();
            throw new AlertWorkerUnavailableError(this.#connectionStatus.value);
        }

        return this.#workerProxy;
    }

    #notifyWorkerUnavailable(): void {
        // The `alerts` toast group has `maxStack: Infinity, persist: true` and
        // the reducer does not dedupe by sourceKey, so we have to check the
        // store ourselves before dispatching — otherwise repeated failures
        // while the worker is down would flood the UI with stuck toasts.
        // This mirrors the pattern in #handleAlertTriggered.
        const sourceKey = WORKER_UNAVAILABLE_SOURCE_KEY;
        const state = this.store.getState();
        const hasActiveToast = Object.values(state.ui.toasts.messages).some(
            (toast) => toast.open && toast.sourceKey === sourceKey,
        );
        if (hasActiveToast || activeWorkerUnavailableNotifications.has(sourceKey)) return;
        if (APP_ELECTRON) {
            activeWorkerUnavailableNotifications.add(sourceKey);
        }
        showToastNotification(this.store, i18next.t('alerts::errors::service-unavailable'), sourceKey);
    }

    /**
     * Tear down the current worker connection and clear alert models.
     */
    #teardownWorker(): void {
        activeWorkerUnavailableNotifications.delete(WORKER_UNAVAILABLE_SOURCE_KEY);
        this.#workerConnectOp.clear();
        if (this.#unsubscribe) {
            this.#unsubscribe();
            this.#unsubscribe = null;
        }
        this.#workerProxy = null;
        this.#currentWorkerId = undefined;
        this.#connectionStatus.value = 'disconnected';
        // Clear stale alert models from the previous user
        for (const model of this.#conditionAlerts.value) {
            model[Symbol.dispose]();
        }
        this.#conditionAlerts.value = [];
        for (const model of this.#ideaAlerts.value) {
            model[Symbol.dispose]();
        }
        this.#ideaAlerts.value = [];
    }

    #handleEvent(event: AlertWorkerEvent): void {
        if (TRACE) {
            this.logger.trace('Received worker event', { type: event.type, event });
        }

        switch (event.type) {
            case 'state-changed':
                // Map worker state to connection status
                switch (event.state) {
                    case 'authenticated':
                        this.#connectionStatus.value = 'connected';
                        break;
                    case 'connecting':
                        this.#connectionStatus.value = 'connecting';
                        break;
                    case 'anonymous':
                        this.#connectionStatus.value = 'disconnected';
                        break;
                    case 'error':
                        this.#connectionStatus.value = 'error';
                        break;
                    case 'initial':
                        this.#connectionStatus.value = 'disconnected';
                        break;
                }
                break;

            case 'alerts-updated':
                if (TRACE) {
                    this.logger.trace('Processing alerts-updated', {
                        payloadLength: event.payload?.length,
                        payload: event.payload,
                    });
                }
                try {
                    this.#syncAlertModels(event.payload);
                } catch (error) {
                    this.logger.error('Failed to sync alert models from alerts-updated event', { error });
                }
                break;

            case 'alert-triggered':
                if (this.#isPrimary) {
                    this.#handleAlertTriggered(event.payload);
                }
                break;

            case 'alert-state-changed':
                this.#updateAlertTriggeredState(event.payload.alertId, event.payload.isTriggered);
                break;

            case 'error':
                this.logger.error('Worker error', { error: event.error });
                break;
        }
    }

    async addAlert(data: NewAlertDefinition): Promise<AlertDefinition> {
        try {
            const proxy = this.#getWorkerProxy();
            const wire = await proxy.createAlert(data);
            const alertDefinition = AlertDefinitionSchema.parse(wire);
            if (TRACE) {
                this.logger.trace('Alert created', { id: alertDefinition.id });
            }
            return alertDefinition;
        } catch (e) {
            if (e instanceof AlertWorkerUnavailableError) {
                this.logger.warn('Alert operation attempted while worker unavailable', {
                    op: 'addAlert',
                    connectionStatus: this.connectionStatus.peek(),
                });
            }
            throw e;
        }
    }

    async updateAlert(id: string, data: Partial<NewAlertDefinition>): Promise<AlertDefinition> {
        try {
            const proxy = this.#getWorkerProxy();
            const wire = await proxy.updateAlert(id, data);
            const alertDefinition = AlertDefinitionSchema.parse(wire);
            if (TRACE) {
                this.logger.trace('Alert updated', { id });
            }
            return alertDefinition;
        } catch (e) {
            if (e instanceof AlertWorkerUnavailableError) {
                this.logger.warn('Alert operation attempted while worker unavailable', {
                    op: 'updateAlert',
                    connectionStatus: this.connectionStatus.peek(),
                });
            }
            throw e;
        }
    }

    async removeAlert(id: string): Promise<void> {
        try {
            const proxy = this.#getWorkerProxy();
            await proxy.deleteAlert(id);
            if (TRACE) {
                this.logger.trace('Alert removed', { id });
            }
        } catch (e) {
            if (e instanceof AlertWorkerUnavailableError) {
                this.logger.warn('Alert operation attempted while worker unavailable', {
                    op: 'removeAlert',
                    connectionStatus: this.connectionStatus.peek(),
                });
            }
            throw e;
        }
    }

    async invalidateAlerts(): Promise<void> {
        try {
            const proxy = this.#getWorkerProxy();
            await proxy.invalidateAlerts();
            if (TRACE) {
                this.logger.trace('Alert definitions cache invalidated');
            }
        } catch (e) {
            if (e instanceof AlertWorkerUnavailableError) {
                this.logger.warn('Alert operation attempted while worker unavailable', {
                    op: 'invalidateAlerts',
                    connectionStatus: this.connectionStatus.peek(),
                });
            }
            throw e;
        }
    }

    async #notifyWorkerQueryCacheUserChange(event: QueryCacheUserChangeEvent): Promise<void> {
        try {
            await this.#workerProxy?.notifyQueryCacheUserChange(event);
        } catch (e) {
            this.logger.warn('Unable to forward query cache user change to alert worker', {
                kind: event.kind,
                error: e,
            });
        }
    }

    #syncAlertModels(wireDefinitions: AlertDefinitionWire[]): void {
        if (TRACE) {
            this.logger.trace('Syncing alert models from wire definitions', {
                wireCount: wireDefinitions.length,
            });
        }

        const definitions: AlertDefinition[] = [];
        for (const wire of wireDefinitions) {
            try {
                definitions.push(AlertDefinitionSchema.parse(wire));
            } catch (parseError) {
                this.logger.error('Failed to parse alert definition, skipping', {
                    wireId: wire.id,
                    wireType: wire.type,
                    wire,
                    error: parseError,
                });
            }
        }

        if (TRACE) {
            this.logger.trace('Parsed alert definitions', {
                parsedCount: definitions.length,
                skippedCount: wireDefinitions.length - definitions.length,
            });
        }

        const conditionDefs = definitions.filter((d): d is typeof d & { type: 'condition' } => d.type === 'condition');
        this.#conditionAlerts.value = syncModelList(conditionDefs, this.#conditionAlerts.value, () =>
            this.conditionAlertModelFactory(),
        );

        const ideaDefs = definitions.filter((d): d is typeof d & { type: 'idea' } => d.type === 'idea');
        this.#ideaAlerts.value = syncModelList(ideaDefs, this.#ideaAlerts.value, () => this.ideaAlertModelFactory());

        if (TRACE) {
            this.logger.trace('Synced alert models', {
                conditionCount: this.#conditionAlerts.value.length,
                ideaCount: this.#ideaAlerts.value.length,
            });
        }
    }

    #updateAlertTriggeredState(alertId: string, isTriggered: boolean): void {
        const model = this.#conditionAlerts.value.find((m) => m.id === alertId);
        if (model) {
            model.setTriggered(isTriggered);
        }
    }

    #handleAlertTriggered(payload: AlertTriggeredPayload): void {
        if (this.#suppressNotifications.value) {
            if (TRACE) {
                this.logger.trace('Notifications suppressed, skipping', { alertId: payload.alertId });
            }
            return;
        }

        this.logger.info('Alert triggered!', { alertId: payload.alertId, instrument: payload.instrument });

        // Generate sourceKey for deduplication - only one toast per alert config
        const sourceKey = `alert:${payload.alertId}`;

        // Check if a toast already exists for this alert
        const state = this.store.getState();
        const hasActiveToast = Object.values(state.ui.toasts.messages).some(
            (toast) => toast.open && toast.sourceKey === sourceKey,
        );

        // When alwaysPlayAlertSounds is true, sounds and TTS fire regardless of
        // toast deduplication. When false, they are suppressed along with the toast.
        const alwaysPlay = this.#alwaysPlaySounds.value;

        if (alwaysPlay || !hasActiveToast) {
            if (payload.tts) {
                const ttsMessage = i18next.t('alerts::tts::condition-triggered', {
                    instrument: spellOutSymbol(payload.instrument),
                    condition: payload.tts.spokenCondition ?? payload.condition,
                });
                this.#speakAlertTts(ttsMessage, payload.tts);
            }

            if (payload.soundType) {
                this.#playAlertSound(payload.soundType);
            }
        }

        if (hasActiveToast) {
            if (TRACE) {
                this.logger.trace('Skipping toast - toast already exists', {
                    alertId: payload.alertId,
                    alwaysPlaySounds: alwaysPlay,
                });
            }
            return;
        }

        if (payload.showToast) {
            const timestamp = this.#formatTriggerTime(Instant.now());
            const model = this.#conditionAlerts.value.find((m) => m.id === payload.alertId);
            const noteStr = model?.definition.value.userNote ? ` - ${model.definition.value.userNote}` : '';
            const message = `${timestamp} - ${payload.instrument}: ${payload.condition}${noteStr}`;
            showToastNotification(this.store, message, sourceKey);
        }
    }

    fireIdeaAlertNotification(
        alertId: string,
        message: string,
        notifications: AlertNotification[],
        context?: { symbol: string; event: 'enter' | 'exit'; ideaName: string },
    ): void {
        if (!this.#isPrimary || this.#suppressNotifications.value) {
            return;
        }

        const sourceKey = `alert:${alertId}:${message}`;
        const showToast = notifications.some((n) => n.type === 'toast');
        const soundNotification = notifications.find((n) => n.type === 'sound');
        const soundType = soundNotification?.type === 'sound' ? soundNotification.sound : undefined;

        if (showToast) {
            const timestamp = this.#formatTriggerTime(Instant.now());
            showToastNotification(this.store, `${timestamp} - ${message}`, sourceKey);
        }

        if (soundType) {
            this.#playAlertSound(soundType);
        }

        const ttsNotification = notifications.find((n) => n.type === 'tts');
        if (ttsNotification?.type === 'tts') {
            const ttsMessage = context
                ? i18next.t('alerts::tts::idea-triggered', {
                      symbol: spellOutSymbol(context.symbol),
                      event: i18next.t(`alerts::tts::event-${context.event}`),
                      ideaName: context.ideaName,
                  })
                : message;
            this.#speakAlertTts(ttsMessage, ttsNotification);
        }
    }

    readonly #triggerTimeFormatter = DateTimeFormatter.ofPattern("MMM d '@' h:mm:ss a").withLocale(Locale.US);

    #formatTriggerTime(instant: Instant): string {
        return instant.atZone(ZoneId.systemDefault()).format(this.#triggerTimeFormatter);
    }

    async #playAlertSound(soundType: string): Promise<void> {
        try {
            let audio = this.#audioCache.get(soundType);
            if (!audio) {
                const soundUrl = await this.#loadSoundUrl(soundType);
                audio = new Audio(soundUrl);
                this.#audioCache.set(soundType, audio);
            }
            audio.volume = 1.0;
            audio.currentTime = 0;
            await audio.play();
        } catch (error) {
            this.logger.error('Failed to play alert sound', { error, soundType });
        }
    }

    async #loadVoices(): Promise<SpeechSynthesisVoice[]> {
        if (!('speechSynthesis' in window)) return [];
        const voices = speechSynthesis.getVoices();
        if (voices.length > 0) return voices;

        // Voices not loaded yet — wait for voiceschanged with a timeout
        return new Promise<SpeechSynthesisVoice[]>((resolve) => {
            const onReady = () => {
                clearTimeout(timer);
                speechSynthesis.removeEventListener('voiceschanged', onReady);
                resolve(speechSynthesis.getVoices());
            };
            const timer = setTimeout(() => {
                speechSynthesis.removeEventListener('voiceschanged', onReady);
                resolve(speechSynthesis.getVoices());
            }, 2000);
            speechSynthesis.addEventListener('voiceschanged', onReady);
        });
    }

    async #speakAlertTts(
        message: string,
        tts: { text?: string; voiceUri?: string; rate: number; pitch: number; volume: number },
    ): Promise<void> {
        try {
            if (!('speechSynthesis' in window)) {
                this.logger.warn('SpeechSynthesis API not available');
                return;
            }
            const spokenText = tts.text || message;
            const utterance = new SpeechSynthesisUtterance(spokenText);
            utterance.rate = tts.rate;
            utterance.pitch = tts.pitch;
            utterance.volume = tts.volume;
            const voices = await this.#voicesReady;
            if (tts.voiceUri) {
                const voice = voices.find((v) => v.voiceURI === tts.voiceUri);
                if (voice) {
                    utterance.voice = voice;
                } else if (TRACE) {
                    this.logger.trace('TTS voice not found, using default', { voiceUri: tts.voiceUri });
                }
            }
            if (tts.voiceUri && !utterance.voice) {
                const googleVoice = voices.find((v) => v.voiceURI.startsWith('Google') && v.lang.startsWith('en'));
                if (googleVoice) {
                    utterance.voice = googleVoice;
                }
            }
            this.logger.info('Speaking TTS alert', { text: spokenText, voice: utterance.voice?.name });
            speechSynthesis.speak(utterance);
        } catch (error) {
            this.logger.error('Failed to speak alert TTS', { error, message });
        }
    }

    async #loadSoundUrl(soundType: string): Promise<string> {
        switch (soundType) {
            case 'tambourine': {
                const { default: url } = await import('src/assets/sounds/tambourineOneShot.webm');
                return url;
            }
            case 'chime1': {
                const { default: url } = await import('src/assets/sounds/new-manual-order-review-1.webm');
                return url;
            }
            case 'chime2': {
                const { default: url } = await import('src/assets/sounds/new-manual-order-review-2.webm');
                return url;
            }
            default:
                throw new Error(`Unknown alert sound type: ${soundType}`);
        }
    }

    simulateTrigger(id: string): void {
        const model = this.#conditionAlerts.value.find((m) => m.id === id);
        if (!model) {
            this.logger.warn('Cannot simulate trigger: alert not found', { alertId: id });
            return;
        }

        const def = model.definition.value;
        const condition = def.condition;

        // Get indicator name and unit from alerting indicators query
        let indicatorName: string | undefined;
        let indicatorUnit: ConcreteIndicatorViewModel['unit'] | undefined;
        if (condition?.type === 'quantitative' && condition.indicator) {
            const indicatorId = condition.indicator.id;
            const alertingIndicators = this.#alertingIndicatorsQuery.signal.value.data;
            if (alertingIndicators) {
                const indicator = alertingIndicators.find((i) => i.id === indicatorId);
                indicatorName = indicator?.name;
                indicatorUnit = indicator?.unit;
                if (TRACE) {
                    this.logger.trace('Indicator lookup for simulate trigger', {
                        indicatorId,
                        foundName: indicatorName,
                        foundUnit: indicatorUnit,
                        indicatorCount: alertingIndicators.length,
                    });
                }
            }
        }

        const conditionStr = condition
            ? formatConditionForDisplay(condition, indicatorName, indicatorUnit)
            : 'triggered';

        // Parse notifications to determine what to simulate
        const parsed = parseNotifications(def.notifications ?? []);

        this.logger.info('Simulating alert trigger', {
            alertId: id,
            showToast: parsed.showToast,
            soundType: parsed.sound,
        });

        this.#handleAlertTriggered({
            alertId: id,
            instrument: instrumentToString(model.instrument),
            condition: conditionStr,
            showToast: parsed.showToast,
            soundType: parsed.sound || undefined,
            tts: parsed.speakTts
                ? {
                      text: parsed.ttsText,
                      voiceUri: parsed.ttsVoiceUri,
                      rate: parsed.ttsRate,
                      pitch: parsed.ttsPitch,
                      volume: parsed.ttsVolume,
                      spokenCondition: condition
                          ? formatConditionForSpeech(condition, indicatorName, indicatorUnit)
                          : 'triggered',
                  }
                : undefined,
        });
    }

    readonly [Symbol.dispose] = () => {
        this.#workerConnectOp.clear();
        this.#disposableStack.dispose();
    };
}
